import { cn } from "../../../lib/utils";

/**
 * THE FACILITATOR OS STATUS CHIP. A label and a tone, and nothing else.
 *
 * ===================== THIS IS THE CONTAINER, NOT THE VOCABULARY ===========
 * Fourteen chip constructions, fourteen class strings, and separately seven
 * surface names for the submitted-but-not-graded state. Those are two different
 * defects and they get two different chunks: this file fixes the container, and
 * the status-vocabulary module (FE-9.2b) fixes the words that flow into it. No
 * label in this codebase is renamed by this file. If a chip said `awaiting
 * review` before it says `awaiting review` after.
 *
 * ===================== FOUR TONES, MAPPED TO PALETTE SEMANTICS =============
 * `info` charlotte, progress and identity. `positive` pear, work turned in.
 * `neutral` the ramp, a count or a fact with no valence. `critical` persimmon,
 * and per the audit persimmon means destructive-or-error ONLY: it is no longer
 * the ordinary commit colour (see `Button.jsx`) and a chip does not reach for it
 * to mean "needs attention" the way `AttentionStage` and the sidebar badge do
 * today. Those two are attention marks, not status chips, and they are out of
 * this primitive's reach by design.
 *
 * There is deliberately NO success tone. Coach forbids positive certification
 * (`coachCopy.js:8-11`: no on-track, no doing-great, no green) and Manage
 * inherits the rule; Review's success-green is the outlier to reconcile in
 * FE-9.3, not a tone to publish here and spread.
 *
 * ===================== THE CHIP BRINGS ITS OWN BACKGROUND ==================
 * Tinted-alpha chips (`bg-persimmon/10`) composite against whatever surface they
 * land on, so their text contrast is a property of the page rather than of the
 * chip: persimmon-on-persimmon/10 measures 4.77:1 over `midnight` and 3.10:1
 * over `astronaut`, which fails AA on the lighter half of the five-tier
 * background system. A solid `background-blackpearl` base makes the number
 * deterministic (persimmon 5.97:1, charlotte 13.85:1, pear 13.91:1, everywhere),
 * and it is the grammar Coach's fact pill already reaches for. Pinned in
 * `StatusChip.test.jsx`.
 */

/**
 * THE SURFACE AND THE TONE MAP ARE PUBLISHED, AND `BASE` CONSUMES THE SURFACE.
 *
 * A second consumer exists that needs this chip's COLOUR and not its GEOMETRY:
 * Coach's member glyph is a fixed 28px circle, and `BASE` hardcodes the pill
 * (`gap-1.5 whitespace-nowrap px-2.5 py-0.5`), so rendering a `StatusChip` there
 * would mean a className fighting four of its own base classes, which is how a
 * primitive acquires twenty-five variants.
 *
 * `CHIP_SURFACE` is DEFINED ONCE AND USED HERE rather than declared beside a
 * `BASE` that still spells the class inline. Two copies drift: change one and
 * the chip, the glyph and both of their tests stay internally green while
 * painting two different backgrounds. One definition makes that impossible
 * where a parity test could only detect it.
 *
 * Two things checked rather than assumed, because this is a shipped primitive:
 * the composed string is byte-identical to the one that shipped, so
 * `StatusChip.test.jsx`'s sorted-class assertion holds unedited; and
 * `bg-background-blackpearl` still appears as a literal in this file, so
 * Tailwind's extractor (a text scan over source) still emits the rule. A class
 * that survives in JS but vanishes from the built CSS is silently inert.
 */
export const CHIP_SURFACE = "bg-background-blackpearl";

const BASE = `inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border ${CHIP_SURFACE} px-2.5 py-0.5 text-xs font-semibold`;

const TONE = {
  info: "border-charlotte/30 text-charlotte",
  positive: "border-pear/30 text-pear",
  critical: "border-persimmon/35 text-persimmon",
  neutral: "border-white/10 text-lv2",
};

export const CHIP_TONE = TONE;

export const STATUS_CHIP_TONES = Object.keys(TONE);

export const StatusChip = ({
  tone = "neutral",
  className = "",
  children,
  ...rest
}) => (
  <span className={cn(BASE, TONE[tone] ?? TONE.neutral, className)} {...rest}>
    {children}
  </span>
);

export default StatusChip;
